import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, Trash2, Plus, RefreshCw, Play, Download, FolderOpen } from "lucide-react";

const StatusIcon = ({ ok }) => ok
  ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
  : <XCircle className="h-4 w-4 text-red-600" />;

const ALL_REGIONS = [
  { id: "paisley", label: "Paisley" },
  { id: "renfrewshire", label: "Renfrewshire" },
  { id: "glasgow", label: "Glasgow" },
];

const ALL_CATEGORIES = ["hospitality", "retail", "events", "services", "charity", "community"];

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [suppressions, setSuppressions] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [processing, setProcessing] = useState(false);
  const [drive, setDrive] = useState(null);
  const [runs, setRuns] = useState([]);
  const [scraping, setScraping] = useState(false);
  const [regions, setRegions] = useState(["paisley", "renfrewshire", "glasgow"]);
  const [categories, setCategories] = useState(ALL_CATEGORIES);
  const [sources, setSources] = useState(["osm", "companies_house"]);
  const [saveToDrive, setSaveToDrive] = useState(false);

  const load = async () => {
    const [s, sup, d, r] = await Promise.all([
      api.get("/settings"),
      api.get("/settings/suppressions"),
      api.get("/drive/status"),
      api.get("/scrape/runs"),
    ]);
    setSettings(s.data);
    setSuppressions(sup.data.items);
    setDrive(d.data);
    setRuns(r.data.items);
  };
  useEffect(() => { load(); }, []);

  const addSup = async () => {
    if (!newEmail) return;
    try {
      await api.post("/settings/suppressions", { email: newEmail, reason: "manual" });
      toast.success("Added to suppression list");
      setNewEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeSup = async (email) => {
    await api.delete(`/settings/suppressions/${encodeURIComponent(email)}`);
    load();
  };
  const processDue = async () => {
    setProcessing(true);
    try {
      const r = await api.post("/campaigns/process-due");
      toast.success(`Processed ${r.data.processed} · sent ${r.data.sent}`);
    } catch (e) { toast.error("Failed"); }
    finally { setProcessing(false); }
  };

  const toggleIn = (arr, val) => arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

  const runScrape = async () => {
    if (regions.length === 0 || sources.length === 0) return toast.error("Pick at least one region and source");
    setScraping(true);
    try {
      const r = await api.post("/scrape/run", {
        regions, sources, categories,
        limit_per_query: 200,
        save_to_drive: saveToDrive,
      });
      toast.success(`Scraped ${r.data.total_scraped} · added ${r.data.inserted} new (${r.data.duplicates} dupes)`);
      if (saveToDrive && !r.data.drive_uploaded) {
        toast.error(`Drive upload failed: ${r.data.drive_error || "not connected"}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scrape failed");
    } finally { setScraping(false); }
  };

  const connectDrive = async () => {
    try {
      const r = await api.get("/drive/connect");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Drive not configured on server");
    }
  };

  const disconnectDrive = async () => {
    await api.post("/drive/disconnect");
    toast.success("Drive disconnected");
    load();
  };

  const downloadRunCsv = (runId) => {
    // Backend endpoint returns CSV — open in new tab (browser will send cookie)
    window.open(`${API_BASE}/scrape/runs/${runId}/csv`, "_blank");
  };

  if (!settings) return <div className="text-sm text-gray-500">Loading…</div>;

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <div className="uppercase-tiny text-gray-500">Configuration</div>
        <h1 className="font-heading text-4xl tracking-tight mt-1">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Integrations */}
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="integrations-card">
          <div className="uppercase-tiny text-gray-500 mb-3">Integrations</div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <div className="font-medium">Resend (email)</div>
                <div className="text-xs text-gray-500">Sender: {settings.sender_email}</div>
              </div>
              <div className="flex items-center gap-2"><StatusIcon ok={settings.resend_configured} /> <span className="text-xs">{settings.resend_configured ? "Configured" : "Not configured"}</span></div>
            </div>
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <div className="font-medium">AI (Claude Sonnet 4.6)</div>
                <div className="text-xs text-gray-500">Lead scoring & personalised intros</div>
              </div>
              <div className="flex items-center gap-2"><StatusIcon ok={settings.llm_configured} /> <span className="text-xs">{settings.llm_configured ? "Configured" : "Not configured"}</span></div>
            </div>
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <div className="font-medium">Companies House (UK)</div>
                <div className="text-xs text-gray-500">Free UK business registry</div>
              </div>
              <div className="flex items-center gap-2"><StatusIcon ok={true} /> <span className="text-xs">Configured</span></div>
            </div>
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <div className="font-medium">OpenStreetMap</div>
                <div className="text-xs text-gray-500">Free — no key required</div>
              </div>
              <div className="flex items-center gap-2"><StatusIcon ok={true} /> <span className="text-xs">Ready</span></div>
            </div>
            <div>
              <div className="font-medium">Google Auth allowlist</div>
              <div className="text-xs text-gray-500 mt-1">{settings.allowed_emails?.length ? settings.allowed_emails.join(", ") : "Any Google account allowed"}</div>
            </div>
          </div>
        </div>

        {/* Drive */}
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="drive-card">
          <div className="uppercase-tiny text-gray-500 mb-3">Google Drive backup</div>
          {!drive?.configured ? (
            <div>
              <div className="flex items-center gap-2 text-sm mb-3"><StatusIcon ok={false} /> Not configured on server</div>
              <p className="text-xs text-gray-600 leading-relaxed">
                To enable Drive CSV backup, add these to <code className="font-mono">/app/backend/.env</code> and restart the backend:
              </p>
              <pre className="mt-2 bg-gray-50 border border-gray-200 rounded-sm p-3 text-[11px] font-mono overflow-x-auto">{`GOOGLE_CLIENT_ID=...
GOOGLE_CLIENT_SECRET=...
GOOGLE_DRIVE_REDIRECT_URI=${window.location.origin}/api/drive/callback
GOOGLE_DRIVE_FOLDER_ID=<optional folder id>`}</pre>
              <p className="text-xs text-gray-500 mt-2">
                Get creds at <a className="underline" target="_blank" rel="noreferrer" href="https://console.cloud.google.com">console.cloud.google.com</a> → OAuth client (Web application) with the redirect URI above. Enable the Drive API.
              </p>
            </div>
          ) : drive.connected ? (
            <div>
              <div className="flex items-center gap-2 text-sm mb-3"><StatusIcon ok={true} /> Connected</div>
              <div className="text-xs text-gray-500 mb-4">Since: {drive.connected_at?.slice(0, 16).replace("T", " ")}</div>
              {drive.folder_id && (
                <a
                  href={`https://drive.google.com/drive/folders/${drive.folder_id}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#1B4332] hover:underline mb-3"
                  data-testid="open-drive-folder"
                >
                  <FolderOpen className="h-3 w-3" /> Open Drive folder
                </a>
              )}
              <Button variant="outline" onClick={disconnectDrive} className="rounded-sm" data-testid="drive-disconnect-button">
                Disconnect
              </Button>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 text-sm mb-3"><StatusIcon ok={false} /> Not connected</div>
              <Button onClick={connectDrive} className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm text-white" data-testid="drive-connect-button">
                Connect Google Drive
              </Button>
            </div>
          )}
        </div>

        {/* Scrape */}
        <div className="bg-white border border-gray-200 rounded-sm p-6 lg:col-span-2" data-testid="scrape-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="uppercase-tiny text-gray-500">Lead discovery</div>
              <div className="font-heading text-xl mt-1">Run a scrape</div>
              <div className="text-xs text-gray-500 mt-1">Pulls businesses from OpenStreetMap + Companies House. New leads are auto-scored and added to your pipeline (dedup against existing).</div>
            </div>
            <Button onClick={runScrape} disabled={scraping} className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm text-white" data-testid="run-scrape-button">
              <Play className="h-4 w-4 mr-1.5" /> {scraping ? "Scraping…" : "Run scrape now"}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-xs uppercase-tiny">Regions</Label>
              <div className="mt-2 space-y-1.5">
                {ALL_REGIONS.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={regions.includes(r.id)} onChange={() => setRegions(toggleIn(regions, r.id))} data-testid={`region-${r.id}`} />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase-tiny">Sources</Label>
              <div className="mt-2 space-y-1.5">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={sources.includes("osm")} onChange={() => setSources(toggleIn(sources, "osm"))} data-testid="source-osm" />
                  OpenStreetMap
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={sources.includes("companies_house")} onChange={() => setSources(toggleIn(sources, "companies_house"))} data-testid="source-companies-house" />
                  Companies House (UK)
                </label>
              </div>
              <div className="mt-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={saveToDrive} onChange={(e) => setSaveToDrive(e.target.checked)} data-testid="save-to-drive-toggle" />
                  Also upload CSV to Google Drive
                </label>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase-tiny">Categories (OSM)</Label>
              <div className="mt-2 space-y-1.5 max-h-40 overflow-auto">
                {ALL_CATEGORIES.map((c) => (
                  <label key={c} className="flex items-center gap-2 text-sm cursor-pointer capitalize">
                    <input type="checkbox" checked={categories.includes(c)} onChange={() => setCategories(toggleIn(categories, c))} data-testid={`category-${c}`} />
                    {c}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Runs */}
          <div className="border-t border-gray-100 pt-4">
            <div className="uppercase-tiny text-gray-500 mb-2">Recent scrape runs</div>
            {runs.length === 0 && <div className="text-xs text-gray-500 py-3">No scrape runs yet.</div>}
            <div className="space-y-1.5">
              {runs.map((r) => (
                <div key={r.run_id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-sm px-3 py-2 text-xs" data-testid={`scrape-run-${r.run_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono">{r.run_id}</div>
                    <div className="text-gray-500">
                      {r.started_at?.slice(0, 16).replace("T", " ")} · {(r.regions || []).join(", ")} · {(r.sources || []).join("+")}
                    </div>
                  </div>
                  <div className="text-gray-700 text-right mr-4">
                    <div>{r.status === "completed" ? `${r.inserted || 0} new / ${r.total_scraped || 0} scraped` : r.status}</div>
                    {r.duplicates > 0 && <div className="text-gray-400 text-[10px]">{r.duplicates} dupes skipped</div>}
                  </div>
                  <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => downloadRunCsv(r.run_id)} data-testid={`download-csv-${r.run_id}`}>
                    <Download className="h-3 w-3 mr-1" /> CSV
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-sm p-6 lg:col-span-2" data-testid="scheduler-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="uppercase-tiny text-gray-500">Sequence scheduler</div>
              <div className="font-heading text-xl mt-1">Send scheduled outreach</div>
              <div className="text-xs text-gray-600 mt-1">A background job runs every 60 seconds. You can also trigger it manually.</div>
            </div>
            <Button onClick={processDue} disabled={processing} className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm text-white" data-testid="process-due-button">
              <RefreshCw className="h-4 w-4 mr-1.5" /> {processing ? "Processing…" : "Process due sends"}
            </Button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-sm p-6 lg:col-span-2" data-testid="suppression-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="uppercase-tiny text-gray-500">Suppression list</div>
              <div className="font-heading text-xl">{suppressions.length} emails</div>
            </div>
            <div className="flex gap-2">
              <Input placeholder="email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="w-64 rounded-sm" data-testid="new-suppression-input" />
              <Button onClick={addSup} className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm text-white" data-testid="add-suppression-button">
                <Plus className="h-4 w-4 mr-1.5" /> Add
              </Button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left uppercase-tiny text-gray-600">
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Reason</th>
                <th className="px-4 py-2 font-medium">Added</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {suppressions.length === 0 && (
                <tr><td colSpan={4} className="text-center text-gray-500 py-6 text-xs">No suppressions.</td></tr>
              )}
              {suppressions.map((s) => (
                <tr key={s.suppression_id} className="border-b border-gray-100" data-testid={`suppression-row-${s.suppression_id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{s.email}</td>
                  <td className="px-4 py-2 text-xs capitalize">{s.reason}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{s.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => removeSup(s.email)} className="text-gray-400 hover:text-red-600" data-testid={`remove-suppression-${s.suppression_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
